import { useEffect } from 'react';

/**
 * Scoped-undo infrastructure (foundation for the Strg+Z feature request).
 *
 * A single most-recent undoable action is held in a module-level registry.
 * Any action that wants to be undoable calls `registerUndo(label, undoFn)`
 * right after it runs; the global Ctrl/Cmd+Z shortcut (mounted once via
 * `useUndoShortcut`) then invokes that undo function.
 *
 * This is deliberately a *scoped* undo, not a global multi-step undo stack:
 * dashboard actions are side-effecting writes against the shared prod data,
 * so each undoable action must provide its own safe inverse. Until actions
 * register an undo, the shortcut is a no-op — there is no behavior change.
 */

export type UndoAction = {
  label: string;
  undo: () => void | Promise<void>;
};

let lastUndo: UndoAction | null = null;

export function registerUndo(label: string, undo: () => void | Promise<void>) {
  lastUndo = { label, undo };
}

export function clearUndo() {
  lastUndo = null;
}

export function hasUndo() {
  return lastUndo !== null;
}

/**
 * Binds Ctrl/Cmd+Z to the most recently registered undo action.
 * Mount once near the app root. `notify` is optional (e.g. a toast).
 */
export function useUndoShortcut(notify?: (message: string) => void) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        !(e.ctrlKey || e.metaKey) ||
        e.shiftKey ||
        e.key.toLowerCase() !== 'z'
      ) {
        return;
      }
      // Never hijack the browser's native undo inside editable fields.
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) ||
          target.isContentEditable)
      ) {
        return;
      }
      if (!lastUndo) return;
      e.preventDefault();
      const action = lastUndo;
      lastUndo = null;
      Promise.resolve(action.undo()).then(
        () => notify?.(`Rückgängig gemacht: ${action.label}`),
        () => notify?.('Rückgängig fehlgeschlagen'),
      );
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [notify]);
}
